import React, { useState } from 'react';
import { Box, Typography, Chip, Divider, Link, Grid } from '@mui/material';
import { LenderApproval, ApprovalBadge } from '../types';
import { DEFAULT_VISIBLE_CONDITIONS } from '../constants';
import { RankBadge } from './RankBadge';

interface ConditionsSectionProps {
  approvals: LenderApproval[];
  conditionsBadges: Record<string, ApprovalBadge>;
}

interface LenderConditionsCardProps {
  approval: LenderApproval;
  badge?: ApprovalBadge;
}

const LenderConditionsCard: React.FC<LenderConditionsCardProps> = ({ approval, badge }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const conditions = approval.approvalTerms.conditions;
  const hasMore = conditions.length > DEFAULT_VISIBLE_CONDITIONS;
  const visibleConditions = isExpanded ? conditions : conditions.slice(0, DEFAULT_VISIBLE_CONDITIONS);
  const hiddenCount = conditions.length - DEFAULT_VISIBLE_CONDITIONS;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {approval.lenderName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {badge && <RankBadge badge={badge} />}
          <Chip
            label={conditions.length}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
          />
        </Box>
      </Box>

      <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
        {visibleConditions.map((condition, index) => (
          <Box component="li" key={condition.id} sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.primary">
              {condition.description}
            </Typography>
          </Box>
        ))}
      </Box>

      {hasMore && !isExpanded && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setIsExpanded(true)}
          sx={{ mt: 1, cursor: 'pointer', display: 'block' }}
        >
          + {hiddenCount} more conditions...
        </Link>
      )}
      {hasMore && isExpanded && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setIsExpanded(false)}
          sx={{ mt: 1, cursor: 'pointer', display: 'block' }}
        >
          Show less
        </Link>
      )}
    </Box>
  );
};

export const ConditionsSection: React.FC<ConditionsSectionProps> = ({ approvals, conditionsBadges }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography fontSize="20px" fontWeight={700} sx={{ mb: 2 }}>
        Conditions & Stipulations
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        {approvals.map(approval => (
          <Grid item xs={12} md key={approval.id}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <LenderConditionsCard approval={approval} badge={conditionsBadges[approval.id]} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
